import { useState } from 'react';
import { Play, Loader2, ShieldAlert } from 'lucide-react';
import { getApiBase } from './apiBase';
import ReconSection from './ReconSection';
import FilterToolbar, { FilterForm } from './FilterToolbar';

export default function EffectiveDateCheck() {
  const [file, setFile] = useState(null);
  const [softWall, setSoftWall] = useState('2026-06-21');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [filterDraft, setFilterDraft] = useState('');
  const [filter, setFilter] = useState('');

  const run = async () => {
    setError('');
    setResult(null);
    setRunning(true);
    try {
      if (!file) throw new Error('Select a file to check.');
      const fd = new FormData();
      fd.append('file', file);
      if (softWall) fd.append('softWall', softWall);
      const base = getApiBase();
      const res = await fetch(`${base}/api/prepayroll/effective-date`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Check failed');
      setResult(data);
    } catch (e) {
      setError(e.message || 'Check failed');
    } finally {
      setRunning(false);
    }
  };

  const flagged = result?.flagged || [];
  const needle = filter.trim().toLowerCase();
  const visible = flagged.filter((r) =>
    !needle
      ? true
      : [r.person, r.wageType, r.effectiveDate, r.recordType, r.detail]
          .map((v) => String(v ?? '').toLowerCase())
          .some((v) => v.includes(needle))
  );

  return (
    <div className="space-y-6">
      <header className="page-hero">
        <p className="section-title">Pre-payroll validation</p>
        <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-100">
          Effective date / soft wall check
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-400">
          Upload any Oracle/ADP/SSL file. Flags rows whose effective date is before the ADP soft
          wall — those changes are blocked in ADP and must be corrected at the source in Oracle.
        </p>
      </header>

      <ReconSection
        title="File & run"
        headerCenter={
          <button type="button" onClick={run} disabled={running} className="btn-primary">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Check dates
          </button>
        }
      >
        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <label className="file-input-wrap cursor-pointer text-sm">
            <span className="font-medium text-slate-200">File to check</span>
            <span className="text-xs text-slate-500">.xlsx / .csv — must have an effective/start date column</span>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="text-xs file:mr-3 file:rounded-md file:border-0 file:bg-slate-800 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-slate-100 hover:file:bg-slate-700"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {file && <span className="truncate text-xs font-mono text-emerald-400">{file.name}</span>}
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium text-slate-200">Soft wall date</span>
            <input
              type="date"
              value={softWall}
              onChange={(e) => setSoftWall(e.target.value)}
              className="rounded-md border border-slate-600 bg-slate-800 px-3 py-1.5 text-sm text-slate-200"
            />
          </label>
        </div>
        {error && <div className="alert-error mt-4">{error}</div>}
      </ReconSection>

      {result && (
        <>
          <div className="flex flex-wrap gap-2">
            <span className="badge-neutral">{result.validDates?.toLocaleString()} dated rows</span>
            <span className={result.flaggedCount ? 'badge-warning inline-flex items-center gap-1' : 'badge-success inline-flex items-center gap-1'}>
              <ShieldAlert className="h-3.5 w-3.5" />
              {result.flaggedCount?.toLocaleString()} before soft wall ({result.softWall})
            </span>
          </div>

          <div className="card-panel overflow-hidden">
            <div className="section-header flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-slate-100">Rows before soft wall</h3>
              <FilterForm onApply={() => setFilter(filterDraft)}>
                <FilterToolbar
                  onClear={() => {
                    setFilterDraft('');
                    setFilter('');
                  }}
                  hasActiveFilters={!!filter.trim()}
                >
                  <input
                    type="search"
                    placeholder="Person, wage type, date…"
                    value={filterDraft}
                    onChange={(e) => setFilterDraft(e.target.value)}
                    className="filter-input min-w-[12rem] text-sm"
                  />
                </FilterToolbar>
              </FilterForm>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table text-xs">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Effective date</th>
                    <th>End date</th>
                    <th>Wage type</th>
                    <th>Record</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-6 text-center text-slate-500">
                        {result.flaggedCount ? 'No rows match the filter.' : 'No rows before the soft wall.'}
                      </td>
                    </tr>
                  ) : (
                    visible.slice(0, 500).map((r, i) => (
                      <tr key={`${r.person}-${r.effectiveDate}-${i}`}>
                        <td className="font-mono">{r.person || '—'}</td>
                        <td>{r.effectiveDate}</td>
                        <td>{r.endDate || '—'}</td>
                        <td className="font-mono">{r.wageType || '—'}</td>
                        <td>{r.recordType || '—'}</td>
                        <td className="max-w-md truncate text-slate-400" title={r.detail}>{r.detail}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
